import React, { useState, useEffect } from 'react';
import { GoogleOAuthProvider, useGoogleLogin, googleLogout } from '@react-oauth/google';
import { Wrapper } from '@googlemaps/react-wrapper';
import {
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { PersonAdd, PersonRemove, Settings, Download, Share, ContentCopy } from '@mui/icons-material';
import axios from 'axios';
import SensorChart from './components/SensorChart';
import WeatherChart from './components/WeatherChart';
import ChatInterface from './components/ChatInterface';
import './App.css';

const GOOGLE_MAPS_API_KEY = 'AIzaSyCXzlwaU0xwY6gNvSGAAcxMtEAqR2bWFC0';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const API_BASE_URL = '/api';


function Map({ sensors, onSensorClick, onMapControlClick, selectedSensors, onBoundsChanged, placingMode }) {
  const [map, setMap] = useState(null);
  const mapRef = React.useRef();
  const placingModeRef = React.useRef(placingMode);
  const hasCenteredRef = React.useRef(false);
  const markersRef = React.useRef([]);

  useEffect(() => {
    placingModeRef.current = placingMode;
    if (map) {
      map.setOptions({ draggableCursor: placingMode ? 'crosshair' : null });
    }
  }, [placingMode, map]);

  useEffect(() => {
    if (mapRef.current && !map) {
      const newMap = new window.google.maps.Map(mapRef.current, {
        zoom: 14,
        center: { lat: 47.5706, lng: -122.2322 }, // Mercer Island center
        mapTypeId: 'roadmap',
        mapId: '74ccbf9227384b5c60b62709',
      });
      setMap(newMap);

      newMap.addListener('click', (event) => {
        if (placingModeRef.current) {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();
          onMapControlClick(lat, lng);
        }
      });

      // Update bounds after map finishes moving (idle fires after fitBounds too)
      newMap.addListener('idle', () => {
        const bounds = newMap.getBounds();
        if (bounds && onBoundsChanged) {
          onBoundsChanged(bounds);
        }
      });
    }
  }, [map, onMapControlClick, onBoundsChanged]);

  useEffect(() => {
    if (map && sensors.length > 0) {
      // Fit map to show all sensors the first time they load
      if (!hasCenteredRef.current) {
        const bounds = new window.google.maps.LatLngBounds();
        sensors.forEach(s => bounds.extend({ lat: s.latitude, lng: s.longitude }));
        map.fitBounds(bounds, 60); // 60px padding
        hasCenteredRef.current = true;
      }

      // Clear existing markers
      markersRef.current.forEach(marker => { marker.map = null; });
      markersRef.current = [];

      const { AdvancedMarkerElement, PinElement } = window.google.maps.marker;

      const newMarkers = sensors.map(sensor => {
        const isSelected = selectedSensors && selectedSensors.has(sensor.sensor_id);

        const pin = new PinElement({
          background: isSelected ? '#9c27b0' : '#e53935',
          borderColor: isSelected ? '#6a0080' : '#b71c1c',
          glyphColor: '#ffffff',
        });

        const marker = new AdvancedMarkerElement({
          position: { lat: sensor.latitude, lng: sensor.longitude },
          map: map,
          title: sensor.name,
          content: pin,
        });

        marker.addListener('gmp-click', () => {
          onSensorClick(sensor);
        });

        marker.sensorId = sensor.sensor_id;
        marker.pin = pin;
        return marker;
      });

      markersRef.current = newMarkers;
    }
  }, [map, sensors, onSensorClick]);

  useEffect(() => {
    if (markersRef.current.length > 0) {
      markersRef.current.forEach(marker => {
        const isSelected = selectedSensors && selectedSensors.has(marker.sensorId);
        if (marker.pin) {
          marker.pin.background = isSelected ? '#9c27b0' : '#e53935';
          marker.pin.borderColor = isSelected ? '#6a0080' : '#b71c1c';
        }
      });
    }
  }, [selectedSensors]);

  return <div ref={mapRef} style={{ width: '100%', height: '500px' }} />;
}

function App() {
  const [sensors, setSensors] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [weatherData, setWeatherData] = useState({});
  const [selectedSensors, setSelectedSensors] = useState(new Set());
  const [allSensorReadings, setAllSensorReadings] = useState({});
  // Which sensors appear on the home conditions card is a global per-sensor
  // `featured` flag (from GET /sensors), toggled via the Google-authed API below.
  // Last-24h readings for the landing time-series chart, keyed by sensor id.
  // Kept separate from allSensorReadings (which the Analytics tab fills with full history).
  const [featuredReadings, setFeaturedReadings] = useState({});
  const [openRegister, setOpenRegister] = useState(false);
  const [openUnregister, setOpenUnregister] = useState(false);
  const [openMapRegister, setOpenMapRegister] = useState(false);
  const [placingMode, setPlacingMode] = useState(false);
  const [googleToken, setGoogleToken] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [shareDialog, setShareDialog] = useState({ open: false, url: '' });
  const [shareCopied, setShareCopied] = useState(false);
  const [sharedReading, setSharedReading] = useState(null);
  // True when the page was opened via a share permalink (?s=...); render only the shared tile.
  const [shareView] = useState(() => new URLSearchParams(window.location.search).has('s'));
  const [shareError, setShareError] = useState(false);

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => setGoogleToken(tokenResponse.access_token),
    onError: () => console.error('Google login failed'),
  });

  const authHeaders = () => googleToken ? { Authorization: `Bearer ${googleToken}` } : {};
  const [openSensorConfig, setOpenSensorConfig] = useState(false);
  const [configSensor, setConfigSensor] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    latitude: '',
    longitude: '',
    user_name: '',
    sensor_type: 'temperature'
  });
  const [mapClickData, setMapClickData] = useState({
    name: '',
    user_name: '',
    latitude: 0,
    longitude: 0,
    sensor_type: 'temperature'
  });

  // Date range filtering state
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 3); // Default to 3 days ago
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0]; // Today
  });
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  // Best fit line state
  const [showBestFit, setShowBestFit] = useState(false);

  useEffect(() => {
    if (!shareView) fetchSensors();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') setPlacingMode(false); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // If the page was opened via a share permalink, load the frozen snapshot
  useEffect(() => {
    const shareId = new URLSearchParams(window.location.search).get('s');
    if (!shareId) return;
    axios.get(`${API_BASE_URL}/shares/${shareId}`)
      .then(res => {
        const data = res.data;
        // Forecast times serialize to ISO strings; the chart expects Date objects.
        if (data?.weather?.forecast) {
          data.weather.forecast = data.weather.forecast.map(d => ({ ...d, time: new Date(d.time) }));
        }
        setSharedReading(data);
      })
      .catch(e => {
        console.error('Failed to load shared reading:', e);
        setShareError(true);
      });
  }, []);

  const fetchWeather = async (sensor) => {
    try {
      const res = await axios.get('https://api.open-meteo.com/v1/forecast', {
        params: {
          latitude: sensor.latitude,
          longitude: sensor.longitude,
          current: 'precipitation,wind_speed_10m',
          hourly: 'temperature_2m,precipitation_probability,wind_speed_10m',
          temperature_unit: 'fahrenheit',
          wind_speed_unit: 'mph',
          forecast_days: 2,
          timezone: 'auto',
        },
      });
      const { current, hourly } = res.data;
      const now = new Date();
      const cutoff = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      const forecast = hourly.time
        .map((t, i) => ({
          time: new Date(t),
          temp: hourly.temperature_2m[i],
          precip: hourly.precipitation_probability[i],
          wind: hourly.wind_speed_10m[i],
        }))
        .filter(d => d.time >= now && d.time <= cutoff);
      setWeatherData(prev => ({
        ...prev,
        [sensor.sensor_id]: {
          wind: current.wind_speed_10m,
          precip: (current.precipitation * 0.0394).toFixed(2),
          forecast,
        },
      }));
    } catch (error) {
      console.error('Failed to fetch weather for sensor', sensor.sensor_id, error);
    }
  };

  const fetchSensors = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/sensors`);
      const sensorsData = response.data || [];
      setSensors(sensorsData);
      sensorsData.forEach(sensor => fetchWeather(sensor));
      // Pull the last 24h for every active sensor so the landing time-series chart
      // has data (windowed to avoid the sensors' 100k+ full histories).
      sensorsData
        .filter(sensor => sensor.latest_recorded_at)
        .forEach(sensor => fetchFeaturedReadings(sensor.sensor_id));
    } catch (error) {
      console.error('Failed to fetch sensors:', error);
    }
  };

  const fetchSensorReadings = async (sensorId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/sensors/${sensorId}/readings`);
      setAllSensorReadings(prev => ({
        ...prev,
        [sensorId]: response.data || []
      }));
    } catch (error) {
      console.error('Failed to fetch sensor readings:', error);
    }
  };

  // Last-24h readings for the landing chart (uses the backend's ?hours window).
  const fetchFeaturedReadings = async (sensorId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/sensors/${sensorId}/readings`, { params: { hours: 24 } });
      setFeaturedReadings(prev => ({
        ...prev,
        [sensorId]: response.data || []
      }));
    } catch (error) {
      console.error('Failed to fetch featured readings:', sensorId, error);
    }
  };

  // Global toggle for whether a sensor appears on the landing conditions card.
  // Optimistically flips the local flag, persists via the Google-authed endpoint,
  // and reverts if the request fails.
  const toggleFeatured = async (sensorId, on) => {
    setSensors(prev => prev.map(s => s.sensor_id === sensorId ? { ...s, featured: on } : s));
    try {
      await axios.put(`${API_BASE_URL}/sensors/${sensorId}/featured`, { featured: on }, { headers: authHeaders() });
    } catch (error) {
      console.error('Failed to update featured flag:', error);
      setSensors(prev => prev.map(s => s.sensor_id === sensorId ? { ...s, featured: !on } : s));
    }
  };

  const handleSensorClick = (sensor) => {
    const newSelectedSensors = new Set(selectedSensors);
    newSelectedSensors.add(sensor.sensor_id);
    setSelectedSensors(newSelectedSensors);
    if (!allSensorReadings[sensor.sensor_id]) {
      fetchSensorReadings(sensor.sensor_id);
    }
  };

  const handleSensorCheckboxChange = (sensorId, checked) => {
    const newSelectedSensors = new Set(selectedSensors);
    
    if (checked) {
      newSelectedSensors.add(sensorId);
      if (!allSensorReadings[sensorId]) {
        fetchSensorReadings(sensorId);
      }
    } else {
      newSelectedSensors.delete(sensorId);
    }
    
    setSelectedSensors(newSelectedSensors);
  };

  const handleRegisterSensor = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/sensors`, {
        name: formData.name,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        user_name: formData.user_name,
        sensor_type: formData.sensor_type
      }, { headers: authHeaders() });
      setOpenRegister(false);
      setFormData({ name: '', latitude: '', longitude: '', user_name: '', sensor_type: 'temperature' });
      
      // Refresh sensors list and fetch readings for the new sensor
      fetchSensors();
    } catch (error) {
      console.error('Failed to register sensor:', error);
    }
  };

  const handleUnregisterSensor = async (sensorId) => {
    try {
      await axios.delete(`${API_BASE_URL}/sensors/${sensorId}`, { headers: authHeaders() });
      fetchSensors();
      
      // Clean up selected sensors and readings
      const newSelectedSensors = new Set(selectedSensors);
      newSelectedSensors.delete(sensorId);
      setSelectedSensors(newSelectedSensors);
      
      const newAllSensorReadings = { ...allSensorReadings };
      delete newAllSensorReadings[sensorId];
      setAllSensorReadings(newAllSensorReadings);
    } catch (error) {
      console.error('Failed to unregister sensor:', error);
    }
  };

  const handleMapControlClick = (lat, lng) => {
    setPlacingMode(false);
    setMapClickData({
      name: '',
      user_name: '',
      latitude: lat,
      longitude: lng
    });
    setOpenMapRegister(true);
  };

  const handleMapRegisterSensor = async () => {
    try {
      await axios.post(`${API_BASE_URL}/sensors`, {
        name: mapClickData.name,
        latitude: mapClickData.latitude,
        longitude: mapClickData.longitude,
        user_name: mapClickData.user_name,
        sensor_type: mapClickData.sensor_type
      }, { headers: authHeaders() });
      setOpenMapRegister(false);
      setMapClickData({ name: '', user_name: '', latitude: 0, longitude: 0, sensor_type: 'temperature' });
      
      // Refresh sensors list and fetch readings for the new sensor
      fetchSensors();
    } catch (error) {
      console.error('Failed to register sensor:', error);
    }
  };

  const handleOpenSensorConfig = (sensor) => {
    setConfigSensor({ ...sensor });
    setOpenSensorConfig(true);
  };

  const handleUpdateSensor = async () => {
    try {
      const updateData = {
        name: configSensor.name || '',
        latitude: configSensor.latitude || 0,
        longitude: configSensor.longitude || 0,
        temp_correction_f: configSensor.temp_correction_f || 0,
        sensor_type: configSensor.sensor_type || 'temperature'
      };
      
      console.log('Updating sensor with data:', updateData);
      
      await axios.put(`${API_BASE_URL}/sensors/${configSensor.sensor_id}`, updateData, { headers: authHeaders() });
      setOpenSensorConfig(false);
      setConfigSensor(null);
      
      // Refresh sensors list
      fetchSensors();
    } catch (error) {
      console.error('Failed to update sensor:', error);
      alert('Failed to update sensor. Please check the console for details.');
    }
  };

  const handleDeleteSensorReadings = async () => {
    if (!window.confirm(`Are you sure you want to erase ALL data for sensor "${configSensor.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/sensors/${configSensor.sensor_id}/readings`, { headers: authHeaders() });

      // Clear readings from local state
      const newAllSensorReadings = { ...allSensorReadings };
      delete newAllSensorReadings[configSensor.sensor_id];
      setAllSensorReadings(newAllSensorReadings);

      setOpenSensorConfig(false);
      setConfigSensor(null);

      alert('All sensor data has been erased successfully.');
    } catch (error) {
      console.error('Failed to delete sensor readings:', error);
      alert('Failed to erase sensor data. Please try again.');
    }
  };

  const handleDeleteSensor = async () => {
    if (!window.confirm(`Are you sure you want to permanently delete sensor "${configSensor.name}" and ALL its data? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/sensors/${configSensor.sensor_id}`, { headers: authHeaders() });

      // Clean up selected sensors and readings
      const newSelectedSensors = new Set(selectedSensors);
      newSelectedSensors.delete(configSensor.sensor_id);
      setSelectedSensors(newSelectedSensors);

      const newAllSensorReadings = { ...allSensorReadings };
      delete newAllSensorReadings[configSensor.sensor_id];
      setAllSensorReadings(newAllSensorReadings);

      setOpenSensorConfig(false);
      setConfigSensor(null);

      // Refresh sensors list
      fetchSensors();

      alert('Sensor has been deleted successfully.');
    } catch (error) {
      console.error('Failed to delete sensor:', error);
      alert('Failed to delete sensor. Please try again.');
    }
  };

  const handleMapBoundsChanged = (bounds) => {
    setMapBounds(bounds);
  };

  const getVisibleSensors = () => {
    if (!mapBounds || !sensors.length) return sensors;
    
    return sensors.filter(sensor => {
      const position = new window.google.maps.LatLng(sensor.latitude, sensor.longitude);
      return mapBounds.contains(position);
    });
  };

  const getLatestTemperature = (sensor) => {
    if (!sensor || !sensor.latest_recorded_at) return null;
    
    const date = new Date(sensor.latest_recorded_at);
    const timeString = date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    return {
      temperature: formatSensorValue(sensor, sensor.latest_temperature_f),
      time: timeString
    };
  };

  const getFilteredReadings = (sensorId) => {
    const readings = allSensorReadings[sensorId];
    if (!readings || readings.length === 0) return [];

    const startDateTime = new Date(startDate + 'T00:00:00Z');
    const endDateTime = new Date(endDate + 'T23:59:59Z');

    return readings.filter(reading => {
      const readingDate = new Date(reading.recorded_at);
      return readingDate >= startDateTime && readingDate <= endDateTime;
    });
  };

  const handleApplyDateRange = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
  };

  const handleExportCSV = () => {
    const csv = buildExportRows().map(row => row.map(v => `"${v}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `lake-temp-${startDate}-to-${endDate}.csv`;
    a.click();
  };

  const buildExportRows = () => {
    const rows = [['Timestamp', 'Sensor', 'Temperature (°F)']];
    Array.from(selectedSensors).forEach(sensorId => {
      const name = sensors.find(s => s.sensor_id === sensorId)?.name || `Sensor ${sensorId}`;
      getFilteredReadings(sensorId).forEach(r => rows.push([r.recorded_at, name, r.temperature_f]));
    });
    rows.slice(1).sort((a, b) => new Date(a[0]) - new Date(b[0]));
    return rows;
  };

  const getTempColor = (tempF) => {
    if (tempF > 75) return '#c62828';
    if (tempF >= 70) return '#2e7d32';
    if (tempF >= 60) return '#0288d1';
    return '#1565c0';
  };

  const getWaveColor = (inches) => {
    if (inches > 36) return '#c62828';
    if (inches > 24) return '#e65100';
    if (inches > 12) return '#0288d1';
    return '#2e7d32';
  };

  const getSensorColor = (sensor, value) =>
    sensor.sensor_type === 'wave_height' ? getWaveColor(value) : getTempColor(value);

  const formatSensorValue = (sensor, value) => {
    const display = (value === null || value === undefined || isNaN(value)) ? value : Number(value).toFixed(1);
    return sensor.sensor_type === 'wave_height' ? `${display} in` : `${display}°F`;
  };

  // Freeze the whole conditions card (every active reading + the forecast as it
  // stood) into a stored snapshot and reference it by a short share link, so the
  // recipient sees exactly these conditions regardless of later sensor activity.
  const handleShareConditions = async () => {
    setShareCopied(false);
    setShareDialog({ open: true, url: '', loading: true, error: false });
    const active = getFeaturedConditions();
    const payload = {
      kind: 'conditions',
      asOf: conditionsAsOf(active),
      rows: active.map(c => ({
        label: c.label,
        value: c.value,
        sensorType: c.sensor.sensor_type,
        sensorId: c.sensor.sensor_id,
        recordedAt: c.recordedAt,
      })),
      weather: representativeWeather(active),
    };
    try {
      const res = await axios.post(`${API_BASE_URL}/shares`, { payload });
      const url = `${window.location.origin}${window.location.pathname}?s=${res.data.id}`;
      setShareDialog({ open: true, url, loading: false, error: false });
    } catch (e) {
      console.error('Failed to create share link:', e);
      setShareDialog({ open: true, url: '', loading: false, error: true });
    }
  };

  const handleCopyShareLink = async () => {
    const url = shareDialog.url;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setShareCopied(true);
    } catch (e) {
      console.error('Failed to copy link:', e);
    }
  };


  // Short, product-friendly label for the compact conditions card. Falls back to
  // the raw sensor name if the name can't be classified.
  const conditionLabel = (sensor) => {
    if (sensor.sensor_type === 'wave_height') return 'Waves';
    const n = (sensor.name || '').toLowerCase();
    if (n.includes('water')) return 'Water Temp';
    if (n.includes('air')) return 'Air Temp';
    return sensor.name;
  };

  // Row order on the card: water temp, then air temp, then waves, then anything else.
  const conditionOrder = (sensor) => {
    if (sensor.sensor_type === 'wave_height') return 2;
    const n = (sensor.name || '').toLowerCase();
    if (n.includes('water')) return 0;
    if (n.includes('air')) return 1;
    return 3;
  };

  // Every sensor that has a reading, ordered for the conditions card.
  const getActiveConditions = () =>
    sensors
      .filter(sensor => sensor.latest_recorded_at)
      .map(sensor => ({
        sensor,
        label: conditionLabel(sensor),
        value: sensor.latest_temperature_f,
        recordedAt: sensor.latest_recorded_at,
      }))
      .sort((a, b) => conditionOrder(a.sensor) - conditionOrder(b.sensor));

  // Sensors shown on the landing card: the globally-featured ones if any are
  // flagged, otherwise all active sensors (so a fresh deploy shows everything).
  const getFeaturedConditions = () => {
    const active = getActiveConditions();
    const featured = active.filter(c => c.sensor.featured);
    return featured.length > 0 ? featured : active;
  };

  // Compact value for a card row, with units: waves in inches, temps in °F.
  const conditionDisplay = (sensorType, value) => {
    const d = (value === null || value === undefined || isNaN(value)) ? value : Number(value).toFixed(1);
    return sensorType === 'wave_height' ? `${d} in` : `${d}°F`;
  };

  // The single forecast shown on the card — the first active sensor that has
  // weather (the sensors here are co-located, so any is representative).
  const representativeWeather = (active) => {
    for (const c of active) {
      if (weatherData[c.sensor.sensor_id]) return weatherData[c.sensor.sensor_id];
    }
    return null;
  };

  // Most recent reading time across the active sensors (ISO string).
  const conditionsAsOf = (active) =>
    active.length
      ? active.reduce((max, c) => (new Date(c.recordedAt) > new Date(max) ? c.recordedAt : max), active[0].recordedAt)
      : null;

  // Renders the compact conditions card, used by both the live landing page and
  // the shared-permalink view. Takes plain row data ({key, label, sensorType,
  // value}) rather than live sensor objects so both callers can share it.
  // `historyChart` (live only) is the previous-24h time series; the shared
  // snapshot has no history, so it's omitted there.
  const renderConditionsCard = ({ rows, asOf, weather, onShare, atShareTime, historyChart }) => {
    const asOfStr = asOf
      ? new Date(asOf).toLocaleString('en-US', {
          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
        })
      : '';
    // One-line summary, short labels (drop the trailing "Temp"): "Water: 74.2°F, ...".
    const summary = rows
      .map(r => `${r.label.replace(/\s*Temp$/i, '')}: ${conditionDisplay(r.sensorType, r.value)}`)
      .join(', ');
    return (
      <Paper elevation={4} sx={{ p: 4, maxWidth: 820, mx: 'auto', textAlign: 'center', position: 'relative' }}>
        {onShare && (
          <IconButton aria-label="Share these conditions" onClick={onShare} size="small" sx={{ position: 'absolute', top: 8, right: 8 }}>
            <Share fontSize="small" />
          </IconButton>
        )}
        <Typography variant="h6" fontWeight={500} gutterBottom>
          Conditions as of {asOfStr}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 500, my: 2 }}>
          {summary}
        </Typography>
        {historyChart && (
          <Box sx={{ mt: 2, mb: 2 }}>{historyChart}</Box>
        )}
        {weather && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 1 }}>
              <Typography variant="body2">Wind: {weather.wind} mph</Typography>
              <Typography variant="body2">Rain: {weather.precip}"</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              24-Hour Forecast{atShareTime ? ' (at share time)' : ''}
            </Typography>
            <WeatherChart data={weather.forecast} />
          </Box>
        )}
      </Paper>
    );
  };

  const speedDialActions = [
    {
      icon: <PersonAdd />,
      name: 'Register Sensor',
      onClick: () => setOpenRegister(true)
    },
    {
      icon: <PersonRemove />,
      name: 'Unregister Sensor',
      onClick: () => setOpenUnregister(true)
    }
  ];

  // Permalink view: a clean page showing only the shared tile, no live home page.
  if (shareView) {
    return (
      <div className="App">
        <Box sx={{ padding: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 4 }}>
            <Typography variant="h4" component="h1">
              {window.APP_CONFIG?.title || "Roxy's Surf Dog Conditions"}
            </Typography>
            <img
              src="/Roxy-Robot.jpg"
              alt="Roxy Robot"
              style={{ height: '2.125rem', width: 'auto', borderRadius: '4px' }}
            />
          </Box>
          <Box sx={{ maxWidth: 560, mx: 'auto' }}>
            {sharedReading ? (
              sharedReading.kind === 'conditions'
                ? renderConditionsCard({
                    rows: (sharedReading.rows || []).map((r, i) => ({
                      key: r.sensorId ?? i,
                      label: r.label,
                      sensorType: r.sensorType,
                      value: r.value,
                    })),
                    asOf: sharedReading.asOf,
                    weather: sharedReading.weather,
                    atShareTime: true,
                  })
                : (() => {
              const sensorShape = { sensor_type: sharedReading.sensorType };
              const color = getSensorColor(sensorShape, sharedReading.value);
              const timeString = new Date(sharedReading.recordedAt).toLocaleString('en-US', {
                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
              });
              const weather = sharedReading.weather;
              return (
                <Paper elevation={4} sx={{ padding: 5, textAlign: 'center', borderTop: `6px solid ${color}` }}>
                  <Typography variant="h5" fontWeight={500} gutterBottom>{sharedReading.name}</Typography>
                  <Typography sx={{ fontSize: '6rem', fontWeight: 700, color, lineHeight: 1, my: 2 }}>
                    {formatSensorValue(sensorShape, sharedReading.value)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">as of {timeString}</Typography>
                  {weather && (
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 1 }}>
                        <Typography variant="body2">Wind: {weather.wind} mph</Typography>
                        <Typography variant="body2">Rain: {weather.precip}"</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        24-Hour Forecast (at share time)
                      </Typography>
                      <WeatherChart data={weather.forecast} />
                    </Box>
                  )}
                </Paper>
              );
                  })()
            ) : (
              <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mt: 6 }}>
                {shareError ? 'This shared reading could not be found.' : 'Loading shared reading…'}
              </Typography>
            )}
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button variant="contained" onClick={() => window.location.assign(window.location.pathname)}>
                View live conditions
              </Button>
            </Box>
          </Box>
        </Box>
      </div>
    );
  }

  return (
    <div className="App">
      <Box sx={{ padding: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1, gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Typography variant="h4" component="h1">
              {window.APP_CONFIG?.title || "Roxy's Surf Dog Conditions"}
            </Typography>
            <img
              src="/Roxy-Robot.jpg"
              alt="Roxy Robot"
              style={{ height: '2.125rem', width: 'auto', borderRadius: '4px', cursor: 'pointer' }}
              onClick={() => setShowImageModal(true)}
            />
          </Box>
          <Box>
            {googleToken ? (
              <Button variant="outlined" size="small" onClick={() => { googleLogout(); setGoogleToken(null); setActiveTab(0); }}>
                Sign Out
              </Button>
            ) : (
              <Button variant="outlined" size="small" onClick={() => handleGoogleLogin()}>
                Sign in with Google
              </Button>
            )}
          </Box>
        </Box>

        {googleToken && (
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ marginBottom: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Surf Conditions" />
            <Tab label="Analytics" />
          </Tabs>
        )}

        {/* Surf Conditions Tab */}
        {(!googleToken || activeTab === 0) && (() => {
          const active = getFeaturedConditions();
          if (active.length === 0) {
            return (
              <Box sx={{ mt: 4 }}>
                <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ marginTop: 8 }}>
                  No sensor readings available yet.
                </Typography>
              </Box>
            );
          }
          // Previous-24h time series for the featured sensors (reuses SensorChart's
          // multi-sensor + dual wave-height axis).
          const historySensorsData = Object.fromEntries(
            active.map(c => [
              c.sensor.sensor_id,
              {
                name: c.sensor.name,
                sensor_type: c.sensor.sensor_type,
                readings: featuredReadings[c.sensor.sensor_id] || [],
              },
            ])
          );
          const hasHistory = active.some(c => (featuredReadings[c.sensor.sensor_id] || []).length > 0);
          return (
            <Box sx={{ mt: 4 }}>
              {renderConditionsCard({
                rows: active.map(c => ({
                  key: c.sensor.sensor_id,
                  label: c.label,
                  sensorType: c.sensor.sensor_type,
                  value: c.value,
                })),
                asOf: conditionsAsOf(active),
                weather: representativeWeather(active),
                onShare: handleShareConditions,
                atShareTime: false,
                historyChart: hasHistory
                  ? <SensorChart sensorsData={historySensorsData} chartHeight={160} showTitle={false} />
                  : null,
              })}
            </Box>
          );
        })()}

        {/* Analytics Tab */}
        {googleToken && activeTab === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ marginBottom: 2 }}>
              Check sensors to view data, click map pins to select sensors, or use the button below to add a sensor by clicking the map.
            </Typography>
            <Button
              variant={placingMode ? 'contained' : 'outlined'}
              color={placingMode ? 'warning' : 'primary'}
              onClick={() => setPlacingMode(m => !m)}
              sx={{ marginBottom: 2 }}
            >
              {placingMode ? 'Click map to place sensor (ESC to cancel)' : 'Add Sensor by Map Click'}
            </Button>

            <Grid container spacing={2} sx={{ marginBottom: 2 }}>
              {/* Map on the left */}
              <Grid item xs={12} md={8}>
                <Paper elevation={3}>
                  <Wrapper apiKey={GOOGLE_MAPS_API_KEY} libraries={['marker']}>
                    <Map
                      sensors={sensors}
                      onSensorClick={handleSensorClick}
                      onMapControlClick={handleMapControlClick}
                      selectedSensors={selectedSensors}
                      onBoundsChanged={handleMapBoundsChanged}
                      placingMode={placingMode}
                    />
                  </Wrapper>
                </Paper>
              </Grid>

              {/* Sensor table on the right */}
              <Grid item xs={12} md={4}>
                <Paper elevation={3} sx={{ height: '500px', overflow: 'auto' }}>
                  <Typography variant="h6" sx={{ padding: 2, borderBottom: 1, borderColor: 'divider' }}>
                    Sensors
                  </Typography>
                  <TableContainer>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" sx={{ width: '48px' }} />
                          <TableCell sx={{ fontWeight: 'bold' }}>Sensor & Location</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Latest Reading</TableCell>
                          <TableCell align="center" sx={{ width: '56px', fontWeight: 'bold' }}>Home</TableCell>
                          <TableCell sx={{ width: '48px', fontWeight: 'bold' }}>Config</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getVisibleSensors().map((sensor) => (
                          <TableRow
                            key={sensor.sensor_id}
                            hover
                            onClick={() => handleSensorCheckboxChange(sensor.sensor_id, !selectedSensors.has(sensor.sensor_id))}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedSensors.has(sensor.sensor_id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleSensorCheckboxChange(sensor.sensor_id, e.target.checked);
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">{sensor.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {sensor.latitude.toFixed(4)}, {sensor.longitude.toFixed(4)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {(() => {
                                const latestData = getLatestTemperature(sensor);
                                if (!latestData) return <Typography variant="body2">-</Typography>;
                                return (
                                  <div>
                                    <Typography variant="body2" fontWeight="medium">{latestData.temperature}</Typography>
                                    <Typography variant="caption" color="text.secondary">{latestData.time}</Typography>
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell align="center" padding="checkbox">
                              <Checkbox
                                title="Show on the home conditions card"
                                checked={!!sensor.featured}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleFeatured(sensor.sensor_id, e.target.checked);
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleOpenSensorConfig(sensor); }}
                                sx={{ padding: '4px' }}
                              >
                                <Settings fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        {getVisibleSensors().length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <Typography variant="body2" color="text.secondary">
                                {sensors.length === 0 ? 'No sensors registered' : 'No sensors in current map view'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>

            {/* Chart and Chat */}
            {selectedSensors.size > 0 && (
              <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
                  <Paper elevation={3} sx={{ padding: 2, flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 2 }}>
                      <Typography variant="h6">Temperature Readings - Selected Sensors</Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                          label="Start Date" type="date" value={tempStartDate}
                          onChange={(e) => setTempStartDate(e.target.value)}
                          InputLabelProps={{ shrink: true }} size="small" sx={{ minWidth: '140px' }}
                        />
                        <TextField
                          label="End Date" type="date" value={tempEndDate}
                          onChange={(e) => setTempEndDate(e.target.value)}
                          InputLabelProps={{ shrink: true }} size="small" sx={{ minWidth: '140px' }}
                        />
                        <Button variant="contained" onClick={handleApplyDateRange} size="small">Apply</Button>
                        <Button variant="outlined" onClick={handleExportCSV} size="small" startIcon={<Download />}>Export CSV</Button>
                        <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 1 }}>
                          <Checkbox checked={showBestFit} onChange={(e) => setShowBestFit(e.target.checked)} size="small" />
                          <Typography variant="body2">Display best fit</Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
                      Showing data from {startDate} to {endDate}
                    </Typography>
                    <SensorChart
                      sensorsData={Object.fromEntries(
                        Array.from(selectedSensors).map(sensorId => {
                          const sensor = sensors.find(s => s.sensor_id === sensorId);
                          return [
                            sensorId,
                            {
                              name: sensor?.name || `Sensor ${sensorId}`,
                              sensor_type: sensor?.sensor_type,
                              readings: getFilteredReadings(sensorId)
                            }
                          ];
                        })
                      )}
                      showBestFit={showBestFit}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                  <Paper elevation={3} sx={{ minHeight: '600px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <ChatInterface apiBaseUrl={API_BASE_URL} />
                  </Paper>
                </Grid>
              </Grid>
            )}

            <SpeedDial
              ariaLabel="Sensor Actions"
              sx={{ position: 'fixed', bottom: 16, right: 16 }}
              icon={<SpeedDialIcon />}
            >
              {speedDialActions.map((action) => (
                <SpeedDialAction
                  key={action.name}
                  icon={action.icon}
                  tooltipTitle={action.name}
                  onClick={action.onClick}
                />
              ))}
            </SpeedDial>
          </Box>
        )}

        <Dialog open={openRegister} onClose={() => setOpenRegister(false)}>
          <DialogTitle>Register New Sensor</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="Sensor Name"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Latitude"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.latitude}
              onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Longitude"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.longitude}
              onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
            />
            <TextField
              margin="dense"
              label="User Name"
              fullWidth
              variant="outlined"
              value={formData.user_name}
              onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Sensor Type</InputLabel>
              <Select
                value={formData.sensor_type}
                label="Sensor Type"
                onChange={(e) => setFormData({ ...formData, sensor_type: e.target.value })}
              >
                <MenuItem value="temperature">Temperature</MenuItem>
                <MenuItem value="wave_height">Wave Height</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenRegister(false)}>Cancel</Button>
            <Button onClick={handleRegisterSensor} variant="contained">Register</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openUnregister} onClose={() => setOpenUnregister(false)}>
          <DialogTitle>Unregister Sensor</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>Select a sensor to unregister:</Typography>
            {getVisibleSensors().map(sensor => (
              <Button
                key={sensor.sensor_id}
                fullWidth
                variant="outlined"
                sx={{ marginBottom: 1 }}
                onClick={() => {
                  handleUnregisterSensor(sensor.sensor_id);
                  setOpenUnregister(false);
                }}
              >
                {sensor.name} ({sensor.latitude}, {sensor.longitude})
              </Button>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenUnregister(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openMapRegister} onClose={() => setOpenMapRegister(false)}>
          <DialogTitle>Add Sensor at Location</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Ctrl+Click detected at coordinates:
            </Typography>
            <TextField
              margin="dense"
              label="Latitude"
              type="number"
              fullWidth
              variant="outlined"
              value={mapClickData.latitude.toFixed(6)}
              disabled
              sx={{ marginBottom: 1 }}
            />
            <TextField
              margin="dense"
              label="Longitude"
              type="number"
              fullWidth
              variant="outlined"
              value={mapClickData.longitude.toFixed(6)}
              disabled
              sx={{ marginBottom: 2 }}
            />
            <TextField
              margin="dense"
              label="Sensor Name"
              fullWidth
              variant="outlined"
              value={mapClickData.name}
              onChange={(e) => setMapClickData({ ...mapClickData, name: e.target.value })}
              autoFocus
            />
            <TextField
              margin="dense"
              label="User Name"
              fullWidth
              variant="outlined"
              value={mapClickData.user_name}
              onChange={(e) => setMapClickData({ ...mapClickData, user_name: e.target.value })}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Sensor Type</InputLabel>
              <Select
                value={mapClickData.sensor_type}
                label="Sensor Type"
                onChange={(e) => setMapClickData({ ...mapClickData, sensor_type: e.target.value })}
              >
                <MenuItem value="temperature">Temperature</MenuItem>
                <MenuItem value="wave_height">Wave Height</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenMapRegister(false)}>Cancel</Button>
            <Button 
              onClick={handleMapRegisterSensor} 
              variant="contained"
              disabled={!mapClickData.name || !mapClickData.user_name}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openSensorConfig} onClose={() => setOpenSensorConfig(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Configure Sensor</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="Sensor ID"
              fullWidth
              variant="outlined"
              value={configSensor?.sensor_id || ''}
              InputProps={{ readOnly: true }}
              sx={{ backgroundColor: '#f5f5f5' }}
            />
            <TextField
              margin="dense"
              label="Sensor Key"
              fullWidth
              variant="outlined"
              value={configSensor?.sensor_key || ''}
              InputProps={{ readOnly: true }}
              sx={{ backgroundColor: '#f5f5f5' }}
            />
            <TextField
              margin="dense"
              label="Sensor Name"
              fullWidth
              variant="outlined"
              value={configSensor?.name || ''}
              onChange={(e) => setConfigSensor({ ...configSensor, name: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Latitude"
              type="number"
              fullWidth
              variant="outlined"
              value={configSensor?.latitude || ''}
              onChange={(e) => setConfigSensor({ ...configSensor, latitude: parseFloat(e.target.value) || 0 })}
              inputProps={{ step: 0.000001 }}
            />
            <TextField
              margin="dense"
              label="Longitude"
              type="number"
              fullWidth
              variant="outlined"
              value={configSensor?.longitude || ''}
              onChange={(e) => setConfigSensor({ ...configSensor, longitude: parseFloat(e.target.value) || 0 })}
              inputProps={{ step: 0.000001 }}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Sensor Type</InputLabel>
              <Select
                value={configSensor?.sensor_type || 'temperature'}
                label="Sensor Type"
                onChange={(e) => setConfigSensor({ ...configSensor, sensor_type: e.target.value })}
              >
                <MenuItem value="temperature">Temperature</MenuItem>
                <MenuItem value="wave_height">Wave Height</MenuItem>
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              label={configSensor?.sensor_type === 'wave_height' ? 'Value Correction (in)' : 'Temperature Correction (°F)'}
              type="number"
              fullWidth
              variant="outlined"
              value={configSensor?.temp_correction_f || ''}
              onChange={(e) => setConfigSensor({ ...configSensor, temp_correction_f: parseFloat(e.target.value) || 0 })}
              inputProps={{ step: 0.01 }}
              helperText="Correction value added to all readings from this sensor"
            />
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={handleDeleteSensor}
                variant="contained"
                color="error"
              >
                Delete Sensor
              </Button>
              <Button
                onClick={handleDeleteSensorReadings}
                variant="outlined"
                color="error"
              >
                Erase All Sensor Data
              </Button>
            </Box>
            <Box>
              <Button onClick={() => setOpenSensorConfig(false)} sx={{ marginRight: 1 }}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdateSensor}
                variant="contained"
                disabled={!configSensor?.name}
              >
                OK
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Share link dialog */}
        <Dialog open={shareDialog.open} onClose={() => setShareDialog({ open: false, url: '' })} maxWidth="sm" fullWidth>
          <DialogTitle>Share this reading</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Anyone who opens this link will see this exact reading as it is right now.
            </Typography>
            {shareDialog.loading ? (
              <Typography variant="body2" sx={{ mt: 1 }}>Generating link…</Typography>
            ) : shareDialog.error ? (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                Couldn't create a share link. Please try again.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                <TextField
                  value={shareDialog.url}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: true }}
                  onFocus={(e) => e.target.select()}
                />
                <Button variant="contained" startIcon={<ContentCopy />} onClick={handleCopyShareLink} sx={{ whiteSpace: 'nowrap' }}>
                  {shareCopied ? 'Copied!' : 'Copy'}
                </Button>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShareDialog({ open: false, url: '' })}>Close</Button>
          </DialogActions>
        </Dialog>

      {process.env.REACT_APP_BUILD_TAG && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', padding: '8px 0 4px' }}>
          build {process.env.REACT_APP_BUILD_TAG}
        </Typography>
      )}
      {showImageModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            cursor: 'zoom-out',
          }}
          onClick={() => setShowImageModal(false)}
        >
          <button
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'none',
              border: 'none',
              color: '#ffffff',
              fontSize: '48px',
              cursor: 'pointer',
              lineHeight: '1',
              padding: '8px',
              outline: 'none',
              zIndex: 10000,
            }}
            onClick={() => setShowImageModal(false)}
          >
            &times;
          </button>
          <div
            style={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src="/Roxy-Robot.jpg"
              alt="Roxy Robot Full"
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              }}
            />
          </div>
        </div>
      )}
      </Box>
    </div>
  );
}

function AppWithAuth() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <App />
    </GoogleOAuthProvider>
  );
}

export default AppWithAuth;
